import { Box, Typography } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

interface FormHtmlEditProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  helperText?: string;
  minRows?: number;
  disabled?: boolean;
}

function FormHtmlEdit<TFieldValues extends FieldValues>({
  control,
  name,
  helperText,
  disabled,
}: FormHtmlEditProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Box sx={{ position: "relative", width: "100%" }}>
          <Editor
            /* use self-hosted TinyMCE build; copy the `tinymce` directory (from the npm package) to
               your `public/tinymce` folder or another static path and point `tinymceScriptSrc`
               to it. this avoids loading from Tiny's CDN and works offline. */
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            licenseKey="gpl"
            value={String(field.value || "")}
            onEditorChange={field.onChange}
            disabled={disabled}
            textareaName="opisHtml"
            aria-label="Opis HTML"
            init={{
              height: 300,
              menubar: true,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "preview",
                "anchor",
                "searchreplace",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "undo redo | blocks | " +
                "bold italic forecolor | alignleft aligncenter " +
                "alignright alignjustify | bullist numlist outdent indent | " +
                "removeformat | help",
              content_style: "body { font-family:Roboto,Arial,sans-serif; font-size:14px }",
              branding: false,
            }}
          />
          {fieldState.error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {fieldState.error.message}
            </Typography>
          )}
          {!fieldState.error && helperText && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
              {helperText}
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

export default FormHtmlEdit;
